/**
 * Who a workspace is shared with — teams or people, and what each may do.
 *
 * One reading holds the page together, and it is stated at the top rather than
 * left to be discovered: **somebody two rows reach keeps the stronger of the
 * two.** It is the only reading where adding a row can never take access away,
 * which is what makes this page safe to edit while people are working.
 *
 * A row names a **team or a person**, and this page offers both. It used to
 * offer only teams, which made a workspace shared with one named person — the
 * shape every workspace is born with, since whoever published it administers
 * it — something the page could show and not make: the Worker took a `userId`
 * all along, and the only way to send one was from somewhere else.
 *
 * Nothing is shared by default. A workspace arrives with one row — whoever
 * published it, administering it — and every other row is one somebody wrote
 * here, so *sharing* is the act rather than restricting.
 *
 * One row behaves unlike the rest and says why on itself: **the last
 * administrator** does not come off. A workspace whose only administrator was
 * just removed is an object nobody but an owner of the organization can reopen,
 * so the change is refused and says so.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { WORKSPACE_RIGHTS } from '@airlock/contracts/cloud/wire';
import { color, font, radius } from '../../theme/tokens';
import { HeaderButton } from '../../components/Shell';
import { Note, PageBody, PageLede } from '../../components/list';
import { RowAction } from '../../components/primitives';
import { useConfirm } from '../../components/Modal';
import { api, apiPath, prefix } from '../../lib/api';
import { initialsOf } from '../../lib/format';
import type {
  Organization,
  Seat,
  TeamList,
  TeamSummary,
  WorkspaceGrant,
  WorkspaceGrantList,
  WorkspaceRight
} from '../../lib/types';
import { failureText } from './shared';

/** What each right lets somebody do, in the words the row needs. */
const MEANS: Record<WorkspaceRight, string> = {
  read: 'Pull it, and run what it starts.',
  write: 'Pull it, and publish its next version.',
  admin: 'Publish it, archive it, restore it, and decide this page.'
};

const RIGHT_LABEL: Record<WorkspaceRight, string> = { read: 'Read', write: 'Write', admin: 'Administration' };

export function WorkspaceAccess({
  organization,
  workspaceId,
  /** The workspace's name, for what a removal leaves behind; null until the shell knows it. */
  workspaceName,
  /** Whether this account may change these rows at all. */
  mayShare
}: {
  organization: Organization;
  workspaceId: string;
  workspaceName: string | null;
  mayShare: boolean;
}): React.JSX.Element {
  const [grants, setGrants] = useState<WorkspaceGrant[] | null>(null);
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [people, setPeople] = useState<Seat[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Who is being added, as `team:<id>` or `user:<id>`. One control and not two,
  // because a grant names a team **or** a person and exactly one of the two —
  // which is the Worker's own rule, and two selects would be a form where
  // filling both in is a mistake the page has to explain afterwards.
  const [adding, setAdding] = useState('');
  const [dialog, ask] = useConfirm();
  // The row whose right was just changed says « Saved » for 1.6 s (§41.2).
  const [saved, setSaved] = useState<string | null>(null);
  const [addingRight, setAddingRight] = useState<WorkspaceRight>('read');
  const base = apiPath`/orgs/${organization.id}/workspaces/${workspaceId}/grants`;

  const load = useCallback(async (): Promise<void> => {
    const [list, teamList, seatList] = await Promise.all([
      api.get<WorkspaceGrantList>(base),
      // Only an administrator is offered the list of teams and of people;
      // somebody who administers one workspace sees its rows without being told
      // who else exists in the organization.
      mayShare
        ? api.get<TeamList>(apiPath`/orgs/${organization.id}/teams`).catch(() => ({ teams: [] }))
        : Promise.resolve({ teams: [] as TeamSummary[] }),
      mayShare
        ? api.get<{ seats: Seat[] }>(apiPath`/orgs/${organization.id}/seats`).catch(() => ({ seats: [] }))
        : Promise.resolve({ seats: [] as Seat[] })
    ]);
    setGrants(list.grants);
    setTeams(teamList.teams);
    // An invitation nobody has accepted has no account to name in a grant, so
    // what is offered is the seats somebody is actually sitting in.
    setPeople(seatList.seats.filter((seat) => seat.userId && seat.seatStatus === 'assigned'));
  }, [base, organization.id, mayShare]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setGrants([]);
      setError(failureText(failure, 'The access could not be read.'));
    });
  }, [load]);

  const act = async (run: () => Promise<WorkspaceGrantList>): Promise<boolean> => {
    setError(null);
    try {
      setGrants((await run()).grants);
      return true;
    } catch (failure) {
      setError(failureText(failure));
      return false;
    }
  };

  const retitle = async (grant: WorkspaceGrant, right: string): Promise<void> => {
    const done = await act(() =>
      api.send<WorkspaceGrantList>(
        'PUT',
        base,
        grant.team ? { teamId: grant.team.id, right } : { userId: grant.person?.id, right },
        {}
      )
    );
    if (!done) return;
    setSaved(grant.id);
    window.setTimeout(() => setSaved((was) => (was === grant.id ? null : was)), 1600);
  };

  /** A row comes off through a small modal that says who loses what (§46.4). */
  const remove = async (grant: WorkspaceGrant): Promise<void> => {
    const who = grant.team ? grant.team.name : (grant.person?.name ?? grant.person?.email ?? 'somebody');
    const agreed = await ask({
      title: `Remove ${who}?`,
      lede: grant.team
        ? `Its ${grant.team.members} member${grant.team.members === 1 ? '' : 's'} lose the access this row gave them.`
        : `${who} loses the access this row gave them.`,
      goes: [
        `${RIGHT_LABEL[grant.right]} for ${
          grant.team ? `${who}’s ${grant.team.members} member${grant.team.members === 1 ? '' : 's'}` : who
        } — unless another row reaches them`
      ],
      stays: [`Their copies of ${workspaceName ?? 'this workspace'}, on their machines`],
      confirm: 'Remove',
      danger: true
    });
    if (agreed) await act(() => api.del<WorkspaceGrantList>(apiPath`${prefix(base)}/${grant.id}`));
  };

  // The last row with administration cannot be taken off (the Worker refuses
  // it): its select and Remove are held at 40 % with the page's own reason.
  const admins = grants?.filter((grant) => grant.right === 'admin').length ?? 0;
  const LAST_ADMIN = 'The last row with administration cannot be taken off.';

  const freeTeams = teams.filter((team) => !grants?.some((grant) => grant.team?.id === team.id));
  const freePeople = people.filter((seat) => !grants?.some((grant) => grant.person?.id === seat.userId));
  const nobodyLeft = freeTeams.length === 0 && freePeople.length === 0;

  /** Share it with whoever the one control names. */
  const share = (): void => {
    const [kind, id] = adding.split(':');
    if (!id) return;
    void act(() =>
      api.send<WorkspaceGrantList>(
        'PUT',
        base,
        kind === 'team' ? { teamId: id, right: addingRight } : { userId: id, right: addingRight },
        {}
      )
    );
    setAdding('');
    setAddingRight('read');
  };

  const columns = mayShare ? 'minmax(0,1fr) 220px minmax(0,1.1fr) 80px' : 'minmax(0,1fr) 220px minmax(0,1.1fr)';

  const control: React.CSSProperties = {
    height: 34,
    padding: '0 10px',
    border: `1px solid ${color.lineInput}`,
    borderRadius: radius.md,
    background: color.surface,
    fontFamily: 'inherit',
    fontSize: 13,
    color: color.ink
  };

  return (
    <>
      {dialog}
      <PageBody max={1040}>
        <PageLede>
          A team or a person, as many as you like. Somebody two rows reach keeps the stronger of the two — the only
          reading where adding a row never takes access away.
        </PageLede>

        {!mayShare && (
          <Note>
            You can see who this workspace is shared with, and change none of it. Deciding these rows is for whoever
            administers the workspace, or the organization.
          </Note>
        )}

        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: columns,
              gap: 16,
              padding: '0 0 8px',
              fontFamily: font.mono,
              fontSize: 10.5,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: color.inkFaint
            }}
          >
            <span>Who</span>
            <span>Right</span>
            <span>What it allows</span>
            {mayShare && <span />}
          </div>

          {grants === null && <p style={{ fontSize: 13, color: color.inkFaint, margin: 0 }}>Reading the access…</p>}

          {grants?.map((grant) => {
            const held = grant.right === 'admin' && admins <= 1 ? LAST_ADMIN : undefined;
            return (
              <div
                key={grant.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: columns,
                  gap: 16,
                  alignItems: 'center',
                  padding: '11px 0',
                  boxShadow: `inset 0 -1px 0 ${color.hairSoft}`
                }}
              >
                <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Face grant={grant} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontSize: 13.5, color: color.ink }}>
                        {grant.team ? grant.team.name : (grant.person?.name ?? grant.person?.email ?? 'somebody')}
                      </span>
                    </span>
                    <span style={{ display: 'block', fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>
                      {grant.team
                        ? `${grant.team.members} member${grant.team.members === 1 ? '' : 's'}`
                        : (grant.person?.email ?? '')}
                    </span>
                  </span>
                </div>

                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {mayShare ? (
                    <select
                      value={grant.right}
                      aria-label={`What ${grant.team?.name ?? grant.person?.email} may do`}
                      disabled={held !== undefined}
                      title={held}
                      onChange={(event) => void retitle(grant, event.target.value)}
                      style={{
                        width: 168,
                        height: 30,
                        opacity: held ? 0.4 : 1,
                        padding: '0 10px',
                        border: `1px solid ${color.lineInput}`,
                        borderRadius: radius.md,
                        background: color.surface,
                        fontFamily: 'inherit',
                        fontSize: 13,
                        color: color.ink
                      }}
                    >
                      {WORKSPACE_RIGHTS.map((right) => (
                        <option key={right} value={right}>
                          {RIGHT_LABEL[right]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: 13, color: color.inkBody }}>{RIGHT_LABEL[grant.right]}</span>
                  )}
                  {saved === grant.id && <span style={{ fontSize: 12.5, color: color.okText, whiteSpace: 'nowrap' }}>✓ Saved</span>}
                </span>

                <span style={{ fontSize: 12, lineHeight: 1.45, color: color.inkFaint }}>{MEANS[grant.right]}</span>

                {mayShare && (
                  <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <RowAction danger reason={held} onClick={() => void remove(grant)}>
                      Remove
                    </RowAction>
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* The verb sits under the rows it adds to, not in the chrome: what
            "Share it" will do is read off the list it lands in. */}
        {mayShare && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
            <select
              value={adding}
              onChange={(event) => setAdding(event.target.value)}
              aria-label="A team or a person to share with"
              style={{ ...control, minWidth: 260 }}
            >
              <option value="">{nobodyLeft ? 'Everybody is already here' : 'A team or a person'}</option>
              {freeTeams.length > 0 && (
                <optgroup label="Teams">
                  {freeTeams.map((team) => (
                    <option key={team.id} value={`team:${team.id}`}>
                      {team.name} · team
                    </option>
                  ))}
                </optgroup>
              )}
              {freePeople.length > 0 && (
                <optgroup label="People">
                  {freePeople.map((seat) => (
                    <option key={seat.id} value={`user:${seat.userId ?? ''}`}>
                      {seat.email}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <select
              value={addingRight}
              onChange={(event) => setAddingRight(event.target.value as WorkspaceRight)}
              aria-label="What they may do"
              style={{ ...control, minWidth: 170 }}
            >
              {WORKSPACE_RIGHTS.map((right) => (
                <option key={right} value={right}>
                  {RIGHT_LABEL[right]}
                </option>
              ))}
            </select>
            <span style={{ flex: 1, minWidth: 160, fontSize: 12, color: color.inkFaint }}>{MEANS[addingRight]}</span>
            <HeaderButton primary reason={adding ? undefined : 'Pick a team or a person first'} onClick={share}>
              Share it
            </HeaderButton>
          </div>
        )}

        {error && <p style={{ fontSize: 13, color: color.badText, margin: 0 }}>{error}</p>}

        <p style={{ margin: 0, maxWidth: 640, fontSize: 12, lineHeight: 1.6, color: color.inkFaint }}>
          An owner or an administrator of {organization.name} administers every workspace it has, whatever this page
          says — they are who reopens one whose own keepers have left. That is also why the last row with
          administration cannot be taken off: it would leave this workspace to them alone.
        </p>
      </PageBody>
    </>
  );
}

/** Initials on the same ground: a team is a square rounded 6, a person a circle (§46.4). */
function Face({ grant }: { grant: WorkspaceGrant }): React.JSX.Element {
  const team = grant.team !== null && grant.team !== undefined;
  return (
    <span
      aria-hidden="true"
      style={{
        flex: 'none',
        width: 24,
        height: 24,
        borderRadius: team ? 6 : radius.chip,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: color.surfaceAlt,
        fontFamily: font.mono,
        fontSize: 9.5,
        fontWeight: 600,
        color: color.inkSubtle
      }}
    >
      {grant.team ? initialsOf(grant.team.name, grant.team.name) : initialsOf(grant.person?.email ?? '', grant.person?.name ?? null)}
    </span>
  );
}
